import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ReplyIcon from '@mui/icons-material/Reply';
import DeleteIcon from '@mui/icons-material/Delete';
import './MessageDropdown.scss';

interface MessageDropdownProps {
  onReply: () => void;
  onDelete: () => void;
  canDelete: boolean;
  isMyMessage?: boolean; // To determine dropdown alignment
}

export const MessageDropdown: React.FC<MessageDropdownProps> = ({ onReply, onDelete, canDelete, isMyMessage = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; position: 'left' | 'right' }>({ 
    top: 0, 
    left: 0, 
    position: 'right' 
  });

  // Update menu position when opening with smart positioning
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const menuWidth = 140; // min-width from CSS
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const padding = 8; // Safety padding from edges

      // Calculate available space on both sides
      const spaceOnRight = viewportWidth - rect.right;
      const spaceOnLeft = rect.left;
      
      // Determine horizontal position based on available space
      let horizontalPosition: 'left' | 'right';
      let horizontalValue: number;

      // For my messages (right side), prefer aligning to the right
      // But if not enough space, align to left
      if (isMyMessage) {
        if (spaceOnRight >= menuWidth + padding) {
          // Enough space on right, align menu's right edge to trigger's right
          horizontalPosition = 'right';
          horizontalValue = viewportWidth - rect.right;
        } else if (spaceOnLeft >= menuWidth + padding) {
          // Not enough on right, align to left
          horizontalPosition = 'left';
          horizontalValue = Math.max(padding, rect.right - menuWidth);
        } else {
          // Constrain to viewport
          horizontalPosition = 'right';
          horizontalValue = padding;
        }
      } else {
        // For received messages (left side), prefer aligning to the left
        // But if not enough space, align to right
        if (spaceOnRight >= menuWidth + padding) {
          // Enough space on right, align menu's left edge to trigger's left
          horizontalPosition = 'left';
          horizontalValue = rect.left;
        } else if (spaceOnLeft >= menuWidth + padding) {
          // Not enough on right, align to right edge
          horizontalPosition = 'right';
          horizontalValue = viewportWidth - rect.right;
        } else {
          // Constrain to viewport
          horizontalPosition = 'left';
          horizontalValue = padding;
        }
      }

      // Vertical position - check if there's space below, otherwise show above
      const menuHeight = 100; // Approximate height
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      let verticalValue: number;
      if (spaceBelow >= menuHeight + padding) {
        verticalValue = rect.bottom + 4;
      } else if (spaceAbove >= menuHeight + padding) {
        verticalValue = rect.top - menuHeight - 4;
      } else {
        // Not enough space either way, show below but constrain
        verticalValue = Math.min(rect.bottom + 4, viewportHeight - menuHeight - padding);
      }

      setMenuPosition({
        top: verticalValue,
        left: horizontalValue,
        position: horizontalPosition
      });
    }
  }, [isOpen, isMyMessage]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleReply = () => {
    setIsOpen(false);
    onReply();
  };

  const handleDelete = () => {
    setIsOpen(false);
    onDelete();
  };

  return (
    <>
      <button
        ref={triggerRef}
        className="message-dropdown__trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Message options"
      >
        <KeyboardArrowDownIcon />
      </button>

      {isOpen && createPortal(
        <div 
          className={`message-dropdown__menu ${menuPosition.position === 'left' ? 'message-dropdown__menu--align-left' : 'message-dropdown__menu--align-right'}`}
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: `${menuPosition.top}px`,
            [menuPosition.position]: `${menuPosition.left}px`
          }}
        >
          <button className="message-dropdown__item" onClick={handleReply}>
            <ReplyIcon className="message-dropdown__icon" />
            <span>Reply</span>
          </button>
          {canDelete && (
            <button className="message-dropdown__item message-dropdown__item--delete" onClick={handleDelete}>
              <DeleteIcon className="message-dropdown__icon" />
              <span>Delete</span>
            </button>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

